import React, { useCallback, useEffect, useRef, useState } from "react";
import { notification } from "antd";
import axios from "axios";
import { useNavigate, useOutletContext } from "react-router-dom";
import { IoGiftOutline, IoPlayOutline, IoRocketOutline } from "react-icons/io5";
import { normalizeLanguage } from "../../services/language";
import { useInterfaceLanguage } from "../../hooks/useInterfaceLanguage";
import { useAuth } from "../../services/AuthContext";

const PLAYBOT_COPY = Object.freeze({
  backToLobby: {
    es: "VOLVER AL LOBBY",
    en: "BACK TO LOBBY",
    de: "ZURÜCK ZUR LOBBY",
    ru: "НАЗАД В ЛОББИ",
  },
  title: {
    es: "JUGAR CON BOT",
    en: "PLAY VS BOT",
    de: "GEGEN BOT SPIELEN",
    ru: "ИГРАТЬ ПРОТИВ БОТА",
  },
  subtitle: {
    es: "Elige tus preferencias y empieza una partida contra la IA.",
    en: "Choose your preferences and start a match against the AI.",
    de: "Wähle deine Einstellungen und starte eine Partie gegen die KI.",
    ru: "Выбери настройки и начни партию против ИИ.",
  },
  scienceModeTitle: {
    es: "MODO CIENCIA",
    en: "SCIENCE MODE",
    de: "WISSENSCHAFTSMODUS",
    ru: "РЕЖИМ «НАУКА»",
  },
  scienceModeSubtitle: {
    es: "La estrategia lo es todo.",
    en: "Strategy is everything.",
    de: "Strategie ist alles.",
    ru: "Стратегия решает всё.",
  },
  scienceModeDetail: {
    es: "El bot calcula cada movimiento.",
    en: "The bot calculates every move.",
    de: "Der Bot berechnet jeden Zug.",
    ru: "Бот рассчитывает каждый ход.",
  },
  chanceModeTitle: {
    es: "MODO AZAR",
    en: "CHANCE MODE",
    de: "ZUFALLSMODUS",
    ru: "РЕЖИМ СЛУЧАЙНОСТИ",
  },
  chanceModeSubtitle: {
    es: "La suerte decide tu camino.",
    en: "Luck decides your path.",
    de: "Das Glück bestimmt deinen Weg.",
    ru: "Удача выбирает твой путь.",
  },
  chanceModeDetail: {
    es: "Juega con dados contra el bot.",
    en: "Play with dice against the bot.",
    de: "Spiele mit Würfeln gegen den Bot.",
    ru: "Играй с кубиками против бота.",
  },
  cienzarModeTitle: {
    es: "MODO CIENZAR",
    en: "CIENZAR MODE",
    de: "CIENZAR-MODUS",
    ru: "РЕЖИМ CIENZAR",
  },
  cienzarModeSubtitle: {
    es: "Estrategia y suerte juntas.",
    en: "Strategy and luck together.",
    de: "Strategie und Glück vereint.",
    ru: "Стратегия и удача вместе.",
  },
  cienzarModeDetail: {
    es: "El desafío completo contra IA.",
    en: "The complete challenge against AI.",
    de: "Die vollständige Herausforderung gegen die KI.",
    ru: "Полный вызов против ИИ.",
  },
  scienceVsAzarModeTitle: {
    es: "CIENCIA V/S AZAR",
    en: "SCIENCE V/S CHANCE",
    de: "WISSENSCHAFT V/S ZUFALL",
    ru: "SCIENCE V/S CHANCE",
  },
  scienceVsAzarModeSubtitle: {
    es: "IA con roles fijos.",
    en: "AI with fixed roles.",
    de: "KI mit festen Rollen.",
    ru: "AI with fixed roles.",
  },
  scienceVsAzarModeDetail: {
    es: "El bot juega ciencia o azar segun el rol que le toque.",
    en: "The bot plays science or chance based on its assigned role.",
    de: "Der Bot spielt Wissenschaft oder Zufall je nach zugewiesener Rolle.",
    ru: "The bot plays science or chance based on its assigned role.",
  },
  playersTitle: {
    es: "JUGADORES",
    en: "PLAYERS",
    de: "SPIELER",
    ru: "ИГРОКИ",
  },
  playersDescription: {
    es: "Elige la cantidad de rivales bot",
    en: "Choose the number of bot opponents",
    de: "Wähle die Anzahl der Bot-Gegner",
    ru: "Выбери количество ботов-соперников",
  },
  oneVsOne: {
    es: "1 VS 1",
    en: "1 VS 1",
    de: "1 GEGEN 1",
    ru: "1 НА 1",
  },
  oneVsOneDetail: {
    es: "Un bot rival",
    en: "One bot opponent",
    de: "Ein Bot-Gegner",
    ru: "Один бот-соперник",
  },
  oneVsTwo: {
    es: "1 VS 2",
    en: "1 VS 2",
    de: "1 GEGEN 2",
    ru: "1 НА 2",
  },
  oneVsTwoDetail: {
    es: "Dos bots rivales",
    en: "Two bot opponents",
    de: "Zwei Bot-Gegner",
    ru: "Два бота-соперника",
  },
  oneVsThree: {
    es: "1 VS 3",
    en: "1 VS 3",
    de: "1 GEGEN 3",
    ru: "1 НА 3",
  },
  oneVsThreeDetail: {
    es: "Tres bots rivales",
    en: "Three bot opponents",
    de: "Drei Bot-Gegner",
    ru: "Три бота-соперника",
  },
  allBotsToggle: {
    es: "SOLO BOTS",
    en: "BOTS ONLY",
    de: "NUR BOTS",
    ru: "ТОЛЬКО БОТЫ",
  },
  allBotsToggleHint: {
    es: "Un bot juega en tu lugar",
    en: "A bot plays in your place",
    de: "Ein Bot spielt an deiner Stelle",
    ru: "Бот играет вместо тебя",
  },
  timePerTurnTitle: {
    es: "TIEMPO POR TURNO",
    en: "TIME PER TURN",
    de: "ZEIT PRO ZUG",
    ru: "ВРЕМЯ НА ХОД",
  },
  timePerTurnDescription: {
    es: "Elige el tiempo para cada turno",
    en: "Choose the time for each turn",
    de: "Wähle die Zeit für jeden Zug",
    ru: "Выбери время для каждого хода",
  },
  minutes: {
    es: "{value} MIN",
    en: "{value} MIN",
    de: "{value} MIN.",
    ru: "{value} МИН",
  },
  difficultyTitle: {
    es: "DIFICULTAD",
    en: "DIFFICULTY",
    de: "SCHWIERIGKEIT",
    ru: "СЛОЖНОСТЬ",
  },
  difficultyDescription: {
    es: "Elige el nivel de la IA",
    en: "Choose the AI level",
    de: "Wähle das KI-Level",
    ru: "Выбери уровень ИИ",
  },
  easy: {
    es: "FÁCIL",
    en: "EASY",
    de: "EINFACH",
    ru: "ЛЁГКИЙ",
  },
  easyDetail: {
    es: "Para practicar",
    en: "For practice",
    de: "Zum Üben",
    ru: "Для тренировки",
  },
  medium: {
    es: "MEDIO",
    en: "MEDIUM",
    de: "MITTEL",
    ru: "СРЕДНИЙ",
  },
  mediumDetail: {
    es: "Desafío equilibrado",
    en: "Balanced challenge",
    de: "Ausgewogene Herausforderung",
    ru: "Сбалансированный вызов",
  },
  hard: {
    es: "DIFÍCIL",
    en: "HARD",
    de: "SCHWER",
    ru: "СЛОЖНЫЙ",
  },
  hardDetail: {
    es: "Solo para expertos",
    en: "Only for experts",
    de: "Nur für Experten",
    ru: "Только для экспертов",
  },
  unfair: {
    es: "INJUSTO",
    en: "UNFAIR",
    de: "UNFAIR",
    ru: "НЕСПРАВЕДЛИВЫЙ",
  },
  unfairDetail: {
    es: "Máxima presión",
    en: "Maximum pressure",
    de: "Maximaler Druck",
    ru: "Максимальное давление",
  },
  goToShop: {
    es: "IR A LA TIENDA",
    en: "GO TO SHOP",
    de: "ZUM SHOP",
    ru: "В МАГАЗИН",
  },
  shopDescription: {
    es: "Consigue nuevos estilos para tus partidas",
    en: "Get new styles for your matches",
    de: "Hol dir neue Designs für deine Partien",
    ru: "Получи новые стили для своих партий",
  },
  play: {
    es: "JUGAR",
    en: "PLAY",
    de: "SPIELEN",
    ru: "ИГРАТЬ",
  },
  opening: {
    es: "ABRIENDO...",
    en: "OPENING...",
    de: "WIRD GEÖFFNET...",
    ru: "ОТКРЫВАЕТСЯ...",
  },
  championshipTitle: {
    es: "CAMPEONATO MUNDIAL XIMA 2026",
    en: "XIMA WORLD CHAMPIONSHIP 2026",
    de: "XIMA-WELTMEISTERSCHAFT 2026",
    ru: "ЧЕМПИОНАТ МИРА ПО XIMA 2026",
  },
  championshipDescription: {
    es: "Entrena con bots y llega preparado",
    en: "Train with bots and arrive prepared",
    de: "Trainiere mit Bots und komm vorbereitet an",
    ru: "Тренируйся с ботами и приходи подготовленным",
  },
  modeSectionLabel: {
    es: "Modo de juego con bot",
    en: "Bot game mode",
    de: "Bot-Spielmodus",
    ru: "Режим игры с ботом",
  },
  missingBotMatchId: {
    es: "No se recibió el identificador de la partida.",
    en: "The match identifier was not received.",
    de: "Die Kennung der Partie wurde nicht empfangen.",
    ru: "Идентификатор партии не был получен.",
  },
  startErrorTitle: {
    es: "No se pudo iniciar el juego",
    en: "The game could not be started",
    de: "Das Spiel konnte nicht gestartet werden",
    ru: "Не удалось начать игру",
  },
  startErrorDescription: {
    es: "Intenta iniciar la partida con bot nuevamente.",
    en: "Try starting the bot match again.",
    de: "Versuche erneut, die Partie gegen den Bot zu starten.",
    ru: "Попробуй снова начать партию с ботом.",
  },
});

const getPlayBotCopy = (language, key, values = {}) => {
  const normalizedLanguage = normalizeLanguage(language);
  const entry = PLAYBOT_COPY[key] || {};
  let copy = entry[normalizedLanguage] || entry.es || entry.en || "";

  Object.entries(values || {}).forEach(([name, value]) => {
    copy = copy.split(`{${name}}`).join(String(value));
  });

  return copy;
};

const BOT_GAME_MODE_OPTIONS = [
  {
    id: "botlocalfree",
    titleKey: "scienceModeTitle",
    subtitleKey: "scienceModeSubtitle",
    detailKey: "scienceModeDetail",
    icon: "/mode-icons/free.png",
    className: "xima-create-mode-science",
  },
  {
    id: "botlocaldice",
    titleKey: "chanceModeTitle",
    subtitleKey: "chanceModeSubtitle",
    detailKey: "chanceModeDetail",
    icon: "/mode-icons/dice.png",
    className: "xima-create-mode-luck",
  },
  {
    id: "botlocalcienzar",
    titleKey: "cienzarModeTitle",
    subtitleKey: "cienzarModeSubtitle",
    detailKey: "cienzarModeDetail",
    icon: "/mode-icons/cienzar.png",
    className: "xima-create-mode-cienzar",
  },
  {
    id: "botlocalsciencevsazar",
    titleKey: "scienceVsAzarModeTitle",
    subtitleKey: "scienceVsAzarModeSubtitle",
    detailKey: "scienceVsAzarModeDetail",
    icon: "/mode-icons/cienzar.png",
    className: "xima-create-mode-sciencevsazar",
  },
];

const BOT_PLAYER_OPTIONS = [
  {
    value: 2,
    labelKey: "oneVsOne",
    detailKey: "oneVsOneDetail",
    icon: "/mode-icons/players-1v1.png",
  },
  {
    value: 3,
    labelKey: "oneVsTwo",
    detailKey: "oneVsTwoDetail",
    icon: "/mode-icons/players-1v2.png",
  },
  {
    value: 4,
    labelKey: "oneVsThree",
    detailKey: "oneVsThreeDetail",
    icon: "/mode-icons/players-1v3.png",
  },
];

const BOT_TIME_OPTIONS = [15, 20, 30, 60];
const LEGACY_BOT_TIME_MAP = new Map([
  [1, 15],
  [2, 20],
  [5, 30],
  [10, 60],
]);
const PLAYBOT_PREFERENCES_STORAGE_KEY = "xima_playbot_preferences_v1";
const BOT_DIFFICULTY_OPTIONS = [
  { value: "easy", labelKey: "easy", detailKey: "easyDetail" },
  { value: "medium", labelKey: "medium", detailKey: "mediumDetail" },
  { value: "hard", labelKey: "hard", detailKey: "hardDetail" },
  { value: "injusto", labelKey: "unfair", detailKey: "unfairDetail" },
];

const clampPlayers = (value) => Math.max(2, Math.min(4, Number(value) || 2));
const clampBotZoom = (value) => Math.min(1.08, Math.max(0.76, value));

const normalizeBotMode = (value) =>
  BOT_GAME_MODE_OPTIONS.some((option) => option.id === value) ? value : "botlocalfree";

const normalizeDifficulty = (value) =>
  BOT_DIFFICULTY_OPTIONS.some((option) => option.value === value) ? value : "medium";
const PLAYBOT_DEFAULT_PREFERENCES = {
  mode: "botlocalcienzar",
  players: 2,
  turnTime: 30,
  difficulty: "medium",
};

const normalizeBotTime = (value) => {
  const parsed = Number(value);
  if (BOT_TIME_OPTIONS.includes(parsed)) return parsed;
  return LEGACY_BOT_TIME_MAP.get(parsed) || PLAYBOT_DEFAULT_PREFERENCES.turnTime;
};

const readPlayBotPreferences = () => {
  if (typeof window === "undefined") {
    return PLAYBOT_DEFAULT_PREFERENCES;
  }

  try {
    const rawValue = window.localStorage.getItem(PLAYBOT_PREFERENCES_STORAGE_KEY);
    if (!rawValue) {
      return PLAYBOT_DEFAULT_PREFERENCES;
    }

    const parsed = JSON.parse(rawValue);
    if (!parsed || typeof parsed !== "object") {
      return PLAYBOT_DEFAULT_PREFERENCES;
    }

    return {
      mode: normalizeBotMode(parsed.mode),
      players: clampPlayers(parsed.players),
      turnTime: normalizeBotTime(parsed.turnTime),
      difficulty: normalizeDifficulty(parsed.difficulty),
    };
  } catch {
    return PLAYBOT_DEFAULT_PREFERENCES;
  }
};

const writePlayBotPreferences = (preferences) => {
  if (typeof window === "undefined") return;
  try {
    const payload = {
      mode: normalizeBotMode(preferences?.mode),
      players: clampPlayers(preferences?.players),
      turnTime: normalizeBotTime(preferences?.turnTime),
      difficulty: normalizeDifficulty(preferences?.difficulty),
    };
    window.localStorage.setItem(PLAYBOT_PREFERENCES_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // Ignore storage failures.
  }
};

const PlayBot = () => {
  const navigate = useNavigate();
  const outletContext = useOutletContext() || {};
  const language = useInterfaceLanguage(outletContext.language);
  const botText = useCallback(
    (key, values) => getPlayBotCopy(language, key, values),
    [language],
  );
  const screenRef = useRef(null);
  const [mode, setMode] = useState(() => readPlayBotPreferences().mode);
  const [players, setPlayers] = useState(() => readPlayBotPreferences().players);
  const [turnTime, setTurnTime] = useState(() => readPlayBotPreferences().turnTime);
  const [difficulty, setDifficulty] = useState(
    () => readPlayBotPreferences().difficulty,
  );
  const [interfaceZoom, setInterfaceZoom] = useState(1);
  const [startingGame, setStartingGame] = useState(false);
  // Solo admins: cantidad de jugadores marcada como "solo bots" (o null).
  // Esta pantalla no va dentro de GameLayout: el rol se lee de la sesion.
  const { user } = useAuth() || {};
  const isAdminViewer =
    outletContext.hasAdminMenuAccess === true ||
    [user?.role, user?.type, user?.permission, user?.accountType, user?.accessLevel]
      .map((value) => String(value || "").trim().toLowerCase())
      .some((value) => value === "admin" || value === "superuser");
  const [allBotsPlayers, setAllBotsPlayers] = useState(null);
  const allBotsMatch = isAdminViewer && allBotsPlayers === clampPlayers(players);

  const toggleAllBotsForPlayers = (value, checked) => {
    setAllBotsPlayers(checked ? value : null);
    if (checked) setPlayers(value);
  };

  useEffect(() => {
    const screen = screenRef.current;
    if (!screen) return undefined;

    const handleWheelZoom = (event) => {
      if (!event.ctrlKey) return;

      event.preventDefault();
      setInterfaceZoom((currentZoom) => {
        const nextZoom = currentZoom + (event.deltaY < 0 ? 0.05 : -0.05);
        return Number(clampBotZoom(nextZoom).toFixed(2));
      });
    };

    screen.addEventListener("wheel", handleWheelZoom, { passive: false });

    return () => {
      screen.removeEventListener("wheel", handleWheelZoom);
    };
  }, []);

  useEffect(() => {
    writePlayBotPreferences({
      mode,
      players,
      turnTime,
      difficulty,
    });
  }, [mode, players, turnTime, difficulty]);

  const startBotGame = async () => {
    if (startingGame) return;

    const selectedMode = normalizeBotMode(mode);
    const selectedPlayers = clampPlayers(players);
    const selectedTime = normalizeBotTime(turnTime);
    const selectedDifficulty = normalizeDifficulty(difficulty);

    try {
      setStartingGame(true);
      const response = await axios.post("/tournaments", {
        tournamentName: "",
        mode: selectedMode,
        memberSize: selectedPlayers,
        havingTime: selectedTime,
        botDifficulty: selectedDifficulty,
        allBotsMatch,
        duration: 1,
        startingTime: new Date().toISOString(),
        isQuickMatch: false,
        isIndependentMatch: false,
        hasMatchPassword: false,
        matchPassword: "",
        tournamentPassword: "",
        joinPassword: "",
        tournamentRewardValue: 0,
        tournamentWinnerPrizeValue: 0,
      });

      const created = response?.data || null;
      const tournamentId = String(created?._id || "").trim();

      if (!tournamentId) {
        throw new Error(botText("missingBotMatchId"));
      }

      navigate(`/game/${tournamentId}?autoplay=1`, {
        state: {
          fromLobbySource: "bot",
          source: "lobbyBotInterface",
          botGamePreferences: {
            mode: selectedMode,
            memberSize: selectedPlayers,
            havingTime: selectedTime,
            botDifficulty: selectedDifficulty,
            allBotsMatch,
          },
        },
      });
    } catch (error) {
      console.error("Error starting bot game:", error);
      notification.error({
        message: botText("startErrorTitle"),
        description:
          error?.response?.data?.message ||
          error?.message ||
          botText("startErrorDescription"),
      });
    } finally {
      setStartingGame(false);
    }
  };

  const openChampionship = () => {
    navigate("/game/play");
  };

  return (
    <main
      ref={screenRef}
      className="xima-create-game-screen xima-bot-game-screen"
      style={{ "--xima-create-zoom": String(interfaceZoom) }}
      onContextMenu={(event) => event.preventDefault()}
    >
      <div className="xima-create-game-overlay" aria-hidden="true" />
      <div className="xima-create-game-shell">
        <header className="xima-create-game-header">
          <div className="xima-create-game-logo" aria-label="XIMA">
            XiMA
          </div>
          <button
            type="button"
            onClick={() => navigate("/game/lobby")}
            className="xima-create-back-button"
          >
            <span aria-hidden="true">&lt;</span>
            {botText("backToLobby")}
          </button>
          <div className="xima-create-game-title">
            <h1>{botText("title")}</h1>
            <p>{botText("subtitle")}</p>
          </div>
        </header>

        <div className="xima-create-controls-stage">
          <div className="xima-create-game-hero-space" aria-hidden="true" />

          <section className="xima-create-mode-grid" aria-label={botText("modeSectionLabel")}>
            {BOT_GAME_MODE_OPTIONS.map((option) => {
              const selected = mode === option.id;
              return (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => setMode(option.id)}
                  className={`xima-create-mode-card ${option.className}${
                    selected ? " xima-create-selected" : ""
                  }`}
                  aria-pressed={selected}
                >
                  <span className="xima-create-mode-icon">
                    <img
                      src={option.icon}
                      alt=""
                      aria-hidden="true"
                      onError={(event) => {
                        event.currentTarget.style.display = "none";
                      }}
                    />
                  </span>
                  <span className="xima-create-mode-copy">
                    <strong>{botText(option.titleKey)}</strong>
                    <small>{botText(option.subtitleKey)}</small>
                    <small>{botText(option.detailKey)}</small>
                  </span>
                </button>
              );
            })}
          </section>

          <section className="xima-create-options-grid xima-bot-options-grid">
            <div className="xima-create-option-panel">
              <div className="xima-create-panel-heading">
                <strong>{botText("playersTitle")}</strong>
                <span>{botText("playersDescription")}</span>
              </div>
              <div className="xima-create-choice-grid xima-create-player-grid">
                {BOT_PLAYER_OPTIONS.map((option) => {
                  const selected = Number(players) === option.value;
                  const playerButton = (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setPlayers(option.value)}
                      className={`xima-create-choice-button${selected ? " xima-create-selected" : ""}`}
                      aria-pressed={selected}
                    >
                      <img
                        src={option.icon}
                        alt=""
                        aria-hidden="true"
                        onError={(event) => {
                          event.currentTarget.style.display = "none";
                        }}
                      />
                      <strong>{botText(option.labelKey)}</strong>
                      <small>{botText(option.detailKey)}</small>
                    </button>
                  );
                  if (!isAdminViewer) return playerButton;

                  const allBotsChecked = allBotsPlayers === option.value && selected;
                  return (
                    <div key={option.value} className="xima-bot-player-slot">
                      {playerButton}
                      <label
                        className={`xima-bot-allbots-toggle${
                          allBotsChecked ? " xima-bot-allbots-active" : ""
                        }`}
                        title={botText("allBotsToggleHint")}
                      >
                        <input
                          type="checkbox"
                          checked={allBotsChecked}
                          onChange={(event) =>
                            toggleAllBotsForPlayers(option.value, event.target.checked)
                          }
                        />
                        <span>{botText("allBotsToggle")}</span>
                      </label>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="xima-create-option-panel">
              <div className="xima-create-panel-heading">
                <strong>{botText("timePerTurnTitle")}</strong>
                <span>{botText("timePerTurnDescription")}</span>
              </div>
              <div className="xima-create-choice-grid xima-create-time-grid">
                {BOT_TIME_OPTIONS.map((value) => {
                  const selected = Number(turnTime) === value;
                  return (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setTurnTime(value)}
                      className={`xima-create-choice-button xima-create-time-button${
                        selected ? " xima-create-selected" : ""
                      }`}
                      aria-pressed={selected}
                    >
                      <strong>{botText("minutes", { value })}</strong>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="xima-create-option-panel">
              <div className="xima-create-panel-heading">
                <strong>{botText("difficultyTitle")}</strong>
                <span>{botText("difficultyDescription")}</span>
              </div>
              <div className="xima-create-choice-grid xima-create-difficulty-grid xima-bot-difficulty-grid">
                {BOT_DIFFICULTY_OPTIONS.map((option) => {
                  const selected = difficulty === option.value;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setDifficulty(option.value)}
                      className={`xima-create-choice-button${selected ? " xima-create-selected" : ""}`}
                      aria-pressed={selected}
                    >
                      <strong>{botText(option.labelKey)}</strong>
                      <small>{botText(option.detailKey)}</small>
                    </button>
                  );
                })}
              </div>
            </div>
          </section>

          <section className="xima-create-action-grid">
            <button
              type="button"
              onClick={() => navigate("/game/store?fromLobbySource=store")}
              className="xima-create-promo-card xima-create-store-card"
            >
              <span className="xima-create-promo-icon" aria-hidden="true">
                <IoGiftOutline />
              </span>
              <span>
                <strong>{botText("goToShop")}</strong>
                <small>{botText("shopDescription")}</small>
              </span>
              <img src="/store/xima-board/xima-board-0.png" alt="" aria-hidden="true" />
            </button>

            <button
              type="button"
              onClick={startBotGame}
              className="xima-create-submit-button xima-bot-submit-button"
              disabled={startingGame}
              aria-busy={startingGame}
            >
              <span aria-hidden="true">
                <IoPlayOutline />
              </span>
              {startingGame ? botText("opening") : botText("play")}
            </button>

            <button
              type="button"
              onClick={openChampionship}
              className="xima-create-promo-card xima-create-championship-card"
            >
              <span className="xima-create-promo-icon" aria-hidden="true">
                <IoRocketOutline />
              </span>
              <span>
                <strong>{botText("championshipTitle")}</strong>
                <small>{botText("championshipDescription")}</small>
              </span>
            </button>
          </section>
        </div>
      </div>
    </main>
  );
};

export default PlayBot;
